import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { Handler, HasOnSuccess, replaceInChain } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { chain, ValidateCountry, ValidateState } from "@insite/client-framework/Store/CommonHandlers/ValidateAddress";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import {
    AddressFieldDisplayCollectionModel,
    AddressFieldDisplayModel,
    BaseAddressModel,
    CustomerValidationDto,
} from "@insite/client-framework/Types/ApiModels";

export type AddressErrors = SafeDictionary<React.ReactNode>;

type HandlerType = Handler<
    {
        address: BaseAddressModel;
        validationRules: CustomerValidationDto;
        addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    } & HasOnSuccess<AddressErrors>,
    {
        addressErrors: AddressErrors;
    }
>;

const getRequiredErrorMessage = (addressFieldDisplayCollection: AddressFieldDisplayModel) =>
    siteMessage("Field_Required", addressFieldDisplayCollection.displayName);

export const StoreValidateCountry: HandlerType = props => {
    const { validationRules, addressFieldDisplayCollection } = props.parameter;
    const countries = getCurrentCountries(props.getState());
    if (!countries) {
        throw new Error("Cannot validate country. The website countries have not been loaded yet.");
    }

    const { country } = props.parameter.address;

    if (
        addressFieldDisplayCollection.country?.isVisible &&
        validationRules.country?.isRequired &&
        countries.length > 1 &&
        !country &&
        addressFieldDisplayCollection.country
    ) {
        props.addressErrors.country = getRequiredErrorMessage(addressFieldDisplayCollection.country);
    }
};

export const StoreValidateState: HandlerType = props => {
    const { country, state } = props.parameter.address;
    const { addressFieldDisplayCollection } = props.parameter;
    const reduxState = props.getState();
    const countries = getCurrentCountries(reduxState);
    const useOneTimeAddress = reduxState.pages.checkoutShipping.useOneTimeAddress;
    const currentUserIsGuest = getCurrentUserIsGuest(reduxState);

    if (!countries) {
        throw new Error("Cannot validate country. The website countries have not been loaded yet.");
    }

    if (!country) {
        return;
    }

    const countryLookup = countries.find(c => c.id === country.id);
    if (
        countries.length > 0 &&
        countryLookup?.states &&
        countryLookup.states.length > 0 &&
        (useOneTimeAddress || currentUserIsGuest) &&
        !state &&
        addressFieldDisplayCollection.state
    ) {
        props.addressErrors.state = getRequiredErrorMessage(addressFieldDisplayCollection.state);
    }
};

replaceInChain(chain, ValidateState, StoreValidateState);
replaceInChain(chain, ValidateCountry, StoreValidateCountry);
